/**
 * Featured collection - PE
 * Behavior port of Hyper's featured-collection.js + product-tabs.js,
 * rebuilt without libraries on top of Ascent's slider-component engine.
 * - Tabs: lazy panels via <template>, fade/translate reveal, design-mode support
 * - Carousel extras: progress bar sync, center-of-image nav offset
 * - Mobile: scroll progress bar for the swipe layout
 * - Reveal: fade-up content motion + heading highlight draw (IntersectionObserver)
 */
if (!customElements.get('paex-featured-collection')) {
  customElements.define(
    'paex-featured-collection',
    class PaexFeaturedCollection extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.panelsWrapper = this.querySelector('.paex-fc__panels');
        this.tabButtons = Array.from(this.querySelectorAll('.paex-fc__tab-btn'));
        this.currentIndex = 0;
        this.abortController = new AbortController();

        this.revealObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add(
                entry.target.classList.contains('paex-motion') ? 'paex-in-view' : 'animate'
              );
              this.revealObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -10% 0px' }
        );

        this.tabButtons.forEach((btn) => {
          btn.addEventListener('click', (event) => {
            event.preventDefault();
            this.setActiveTab(Number(btn.dataset.index));
          });
        });

        if (window.Shopify && Shopify.designMode) {
          document.addEventListener(
            'shopify:block:select',
            (event) => {
              if (!this.contains(event.target) && !this.panelHasBlock(event.target)) return;
              const index = Number(event.target.dataset.index);
              if (!Number.isNaN(index)) this.setActiveTab(index);
            },
            { signal: this.abortController.signal }
          );
        }

        this.wireHeader();
        this.getPanels().forEach((panel) => this.wirePanel(panel));
      }

      disconnectedCallback() {
        if (this.revealObserver) this.revealObserver.disconnect();
        this.abortController.abort();
      }

      panelHasBlock(target) {
        return target && target.closest && target.closest('paex-featured-collection') === this;
      }

      getPanels() {
        return Array.from(this.panelsWrapper.querySelectorAll('.paex-fc__panel'));
      }

      wireHeader() {
        this.querySelectorAll('.paex-fc__header .paex-motion').forEach((el) => this.revealObserver.observe(el));
        this.querySelectorAll('.paex-highlight--hand-drawn-circle, .paex-highlight--thick-underline, .paex-highlight--wave-underline').forEach(
          (el) => this.revealObserver.observe(el)
        );
      }

      setActiveTab(index) {
        if (this.currentIndex === index || !this.tabButtons.length) return;

        const currentButton = this.tabButtons[this.currentIndex];
        const newButton = this.tabButtons[index];
        if (!newButton) return;

        currentButton.classList.remove('active');
        currentButton.setAttribute('aria-selected', 'false');
        newButton.classList.add('active');
        newButton.setAttribute('aria-selected', 'true');

        this.getPanels().forEach((panel) => {
          panel.hidden = true;
        });

        let newPanel = this.panelsWrapper.querySelector(`.paex-fc__panel[data-index="${index}"]`);
        if (!newPanel) {
          const template = this.panelsWrapper.querySelector(`template[data-index="${index}"]`);
          if (!template) return;
          newPanel = template.content.cloneNode(true).firstElementChild;
          this.panelsWrapper.appendChild(newPanel);
          this.wirePanel(newPanel);
        }

        newPanel.hidden = false;
        newPanel.classList.remove('paex-anim-in');
        void newPanel.offsetWidth; /* restart the reveal animation */
        newPanel.classList.add('paex-anim-in');

        this.currentIndex = index;

        this.dispatchEvent(
          new CustomEvent('paex:tabChange', {
            bubbles: true,
            detail: { selectedIndex: index, selectedTab: newPanel },
          })
        );
      }

      wirePanel(panel) {
        panel.querySelectorAll('.paex-motion').forEach((el) => this.revealObserver.observe(el));
        panel.querySelectorAll('.paex-highlight--hand-drawn-circle, .paex-highlight--thick-underline, .paex-highlight--wave-underline').forEach(
          (el) => this.revealObserver.observe(el)
        );

        const slider = panel.querySelector('slider-component');
        if (slider) {
          this.wireSliderProgress(panel, slider);
          if (slider.dataset.paexNav === 'center_image') this.wireCenterImageNav(panel, slider);
        }

        const scrollProgress = panel.querySelector('.paex-fc__scroll-progress');
        if (scrollProgress) this.wireScrollProgress(panel, scrollProgress);
      }

      wireSliderProgress(panel, slider) {
        const bar = panel.querySelector('.paex-fc__controls--bottom .paex-fc__progress-bar');
        if (!bar) return;

        const update = () => {
          const total = slider.slideLength;
          const perPage = slider.slidesPerPage;
          const index = slider.index || 0;
          if (!total || !perPage || total <= perPage) return;
          /* Hyper's progressbar semantics: empty at the start, full at the last page */
          const progress = Math.min(1, Math.max(0, index / (total - perPage)));
          bar.style.setProperty('--paex-progress', progress.toFixed(4));
        };

        /* The engine mounts lazily and fires sliderChanged on install and each slide */
        slider.addEventListener('sliderChanged', update, { signal: this.abortController.signal });
      }

      wireCenterImageNav(panel, slider) {
        const controls = panel.querySelector('.paex-fc__controls');
        if (!controls) return;

        const setOffset = () => {
          /* Center on the product image, not the (taller) banner card — matches Hyper */
          const firstImage = panel.querySelector('.paex-fc__item:not(.paex-fc__item--banner) img');
          if (firstImage && firstImage.clientHeight > 0) {
            controls.style.setProperty('--paex-nav-top', `${Math.round(firstImage.clientHeight / 2)}px`);
          }
        };

        setOffset();
        if (document.readyState !== 'complete') {
          window.addEventListener('load', setOffset, { once: true, signal: this.abortController.signal });
        }
        let resizeFrame;
        window.addEventListener(
          'resize',
          () => {
            cancelAnimationFrame(resizeFrame);
            resizeFrame = requestAnimationFrame(setOffset);
          },
          { passive: true, signal: this.abortController.signal }
        );
      }

      wireScrollProgress(panel, scrollProgress) {
        const target = document.getElementById(scrollProgress.dataset.target) || panel.querySelector('.paex-fc__items');
        const bar = scrollProgress.querySelector('.paex-fc__progress-bar');
        if (!target || !bar) return;

        const update = () => {
          const max = target.scrollWidth;
          if (max <= target.clientWidth) {
            bar.style.setProperty('--paex-progress', '1');
            return;
          }
          const progress = (target.scrollLeft + target.clientWidth) / max;
          bar.style.setProperty('--paex-progress', progress.toFixed(4));
        };

        let scrollFrame;
        target.addEventListener(
          'scroll',
          () => {
            cancelAnimationFrame(scrollFrame);
            scrollFrame = requestAnimationFrame(update);
          },
          { passive: true }
        );
        update();
      }
    }
  );
}
